import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, ShoppingCart, Minus, Plus, ArrowLeft, Truck, Star, Trash2, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const CANONICAL_BASE = 'https://posterfire.com';

const getOptImg = (url, w = 600) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads/')) {
    const f = url.replace('/api/uploads/', '');
    return `${BACKEND}/api/img/${f}?w=${w}&q=80`;
  }
  return url;
};

const getFullImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads/')) return `${CANONICAL_BASE}${url}`;
  if (url.startsWith('http')) return url;
  return `${CANONICAL_BASE}${url}`;
};

const buildAltText = (product) => {
  const parts = [product.title];
  if (product.category) parts.push(product.category);
  parts.push('poster');
  if (product.sizes?.length) {
    const defaultSize = product.sizes[0];
    if (defaultSize) parts.push(`${defaultSize.size} inches`);
  }
  return parts.join(' ');
};

const StarRating = ({ rating, size = 'w-4 h-4', interactive, onRate }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(n => (
      <Star key={n}
        className={`${size} ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-gray-600'} ${interactive ? 'cursor-pointer hover:text-amber-300' : ''}`}
        onClick={interactive ? () => onRate(n) : undefined}
      />
    ))}
  </div>
);

const ProductDetailPage = () => {
  const { id, slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average_rating: 0 });
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);

  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { isAuthenticated, isAdmin, token } = useAuth();
  const inWishlist = product ? isInWishlist(product.id) : false;

  const fetchReviews = useCallback(async (productId) => {
    try {
      const res = await axios.get(`${API}/products/${productId}/reviews`);
      setReviews(res.data.reviews);
      setReviewStats({ total: res.data.total, average_rating: res.data.average_rating });
    } catch {}
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        let response;
        if (slug) {
          response = await axios.get(`${API}/products/by-slug/${slug}`);
        } else if (id) {
          response = await axios.get(`${API}/products/${id}`);
          if (response.data.slug) {
            navigate(`/products/${response.data.slug}`, { replace: true });
            return;
          }
        }
        const p = response.data;
        setProduct(p);
        if (p.sizes?.length > 0) {
          setSelectedSize(p.sizes[0]);
        }
        fetchReviews(p.id);
      } catch {
        toast.error('Product not found');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, slug, navigate, fetchReviews]);

  const productSlug = product?.slug || product?.id || '';
  const canonicalUrl = `${CANONICAL_BASE}/products/${productSlug}`;

  const seoTitle = useMemo(() => {
    if (!product) return '';
    let title = product.title;
    if (title.length > 50) title = title.substring(0, 50);
    return `${title} | PosterFire.com`;
  }, [product]);

  const seoDescription = useMemo(() => {
    if (!product) return '';
    const name = product.title;
    const cat = product.category || '';
    const sizes = product.sizes?.length ? `Available in ${product.sizes.map(s => s.size).join(', ')} sizes. ` : '';
    let desc = `Buy ${name} ${cat} poster. ${sizes}Free shipping available.`;
    if (desc.length > 160) desc = desc.substring(0, 157) + '...';
    return desc;
  }, [product]);

  const jsonLdString = useMemo(() => {
    if (!product) return '';
    const allImgs = [product.image_url, ...(product.additional_images || [])].filter(Boolean).map(getFullImageUrl);
    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.title,
      "description": product.description,
      "image": allImgs,
      "brand": { "@type": "Brand", "name": "PosterFire" },
      "sku": product.sku || product.id,
      "category": product.category,
      "url": canonicalUrl,
      "offers": product.sizes?.length > 0 ? {
        "@type": "AggregateOffer",
        "priceCurrency": "USD",
        "lowPrice": Math.min(...product.sizes.map(s => s.price)).toFixed(2),
        "highPrice": Math.max(...product.sizes.map(s => s.price)).toFixed(2),
        "offerCount": product.sizes.length,
        "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "url": canonicalUrl
      } : {
        "@type": "Offer",
        "priceCurrency": "USD",
        "price": (product.price || 0).toFixed(2),
        "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "url": canonicalUrl
      },
      ...(reviewStats.total > 0 ? {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": reviewStats.average_rating.toFixed(1),
          "reviewCount": reviewStats.total
        }
      } : {})
    };
    return JSON.stringify(ld);
  }, [product, reviewStats.total, reviewStats.average_rating, canonicalUrl]);

  useEffect(() => {
    if (!product) return;
    document.title = seoTitle;

    const setOrCreate = (selector, attr, val, createTag, createAttr) => {
      let el = document.querySelector(selector);
      if (!el && createTag) {
        el = document.createElement(createTag);
        if (createAttr) Object.entries(createAttr).forEach(([k,v]) => el.setAttribute(k,v));
        document.head.appendChild(el);
      }
      if (el) el.setAttribute(attr, val);
      return el;
    };

    // Meta description
    setOrCreate('meta[name="description"]', 'content', seoDescription);
    // Robots
    setOrCreate('meta[name="robots"]', 'content', 'index, follow', 'meta', { name: 'robots' });
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.setAttribute('rel', 'canonical'); document.head.appendChild(canonical); }
    canonical.setAttribute('href', canonicalUrl);

    // Open Graph
    setOrCreate('meta[property="og:title"]', 'content', seoTitle, 'meta', { property: 'og:title' });
    setOrCreate('meta[property="og:description"]', 'content', seoDescription, 'meta', { property: 'og:description' });
    setOrCreate('meta[property="og:image"]', 'content', getFullImageUrl(product.image_url), 'meta', { property: 'og:image' });
    setOrCreate('meta[property="og:url"]', 'content', canonicalUrl, 'meta', { property: 'og:url' });
    setOrCreate('meta[property="og:type"]', 'content', 'product', 'meta', { property: 'og:type' });

    // Twitter
    setOrCreate('meta[name="twitter:title"]', 'content', seoTitle, 'meta', { name: 'twitter:title' });
    setOrCreate('meta[name="twitter:description"]', 'content', seoDescription, 'meta', { name: 'twitter:description' });
    setOrCreate('meta[name="twitter:image"]', 'content', getFullImageUrl(product.image_url), 'meta', { name: 'twitter:image' });

    // JSON-LD
    let jsonLdScript = document.querySelector('#product-jsonld');
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.id = 'product-jsonld';
      jsonLdScript.type = 'application/ld+json';
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = jsonLdString;

    return () => {
      document.title = 'PosterFire — Premium Posters, Wall Art & Home Decor';
      const el = document.querySelector('#product-jsonld');
      if (el) el.remove();
      const can = document.querySelector('link[rel="canonical"]');
      if (can) can.setAttribute('href', 'https://posterfire.com');
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.remove();
    };
  }, [product, seoTitle, seoDescription, canonicalUrl, jsonLdString]);

  const handleAddToCart = async () => {
    try {
      setAddingToCart(true);
      await addToCart(product.id, quantity, selectedSize?.size || '18x24');
      toast.success('Added to cart!');
    } catch (error) {
      toast.error(error.message || 'Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) { toast.error('Please login to add to wishlist'); navigate('/login'); return; }
    try {
      if (inWishlist) { await removeFromWishlist(product.id); toast.success('Removed from wishlist'); }
      else { await addToWishlist(product.id); toast.success('Added to wishlist'); }
    } catch { toast.error('Failed to update wishlist'); }
  };

  const handleSubmitReview = async () => {
    if (!newReview.comment.trim()) { toast.error('Please write a comment'); return; }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/products/${product.id}/reviews`, newReview, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review submitted!');
      setNewReview({ rating: 5, comment: '' });
      fetchReviews(product.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally { setSubmittingReview(false); }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}?product_id=${product.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review deleted');
      fetchReviews(product.id);
    } catch { toast.error('Failed to delete review'); }
  };

  const currentPrice = selectedSize?.price || product?.price || 0;
  const allImages = [product?.image_url, ...(product?.additional_images || [])].filter(Boolean);
  const altText = product ? buildAltText(product) : '';

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="product-detail-loading">
        <div className="grid lg:grid-cols-2 gap-10">
          <Skeleton className="aspect-square w-full max-w-md" />
          <div className="space-y-4"><Skeleton className="h-4 w-24" /><Skeleton className="h-10 w-full" /><Skeleton className="h-20 w-full" /></div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider mb-6 hover:text-fire transition-colors"
        data-testid="back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Image Section */}
        <div>
          <div className="relative poster-image-wrap rounded-lg overflow-hidden max-w-md mx-auto lg:mx-0">
            <img src={getOptImg(allImages[selectedImage] || product.image_url, 800)}
              alt={altText}
              className="w-full aspect-[3/4] object-cover poster-image"
              loading="eager" decoding="async"
              draggable="false" onContextMenu={(e) => e.preventDefault()}
              data-testid="product-image" />
            {product.featured && (
              <span className="absolute top-3 left-3 bg-fire text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded">Featured</span>
            )}
          </div>
          {allImages.length > 1 && (
            <div className="flex gap-2 mt-3 max-w-md mx-auto lg:mx-0" data-testid="image-thumbnails">
              {allImages.map((img, i) => (
                <button key={i} onClick={() => setSelectedImage(i)}
                  className={`w-16 h-20 rounded overflow-hidden border-2 transition-all poster-image-wrap ${
                    selectedImage === i ? 'border-orange-500 shadow-lg shadow-orange-500/20' : 'border-gray-200 hover:border-gray-400'
                  }`}>
                  <img src={getOptImg(img, 100)} alt={`${product.title} thumbnail ${i + 1}`}
                    className="w-full h-full object-cover poster-image"
                    loading="lazy" decoding="async"
                    draggable="false" onContextMenu={(e) => e.preventDefault()} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details Section */}
        <div className="space-y-5">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-500">{product.category}</span>
            <h1 className="font-heading text-3xl md:text-4xl mt-1 leading-tight" data-testid="product-title">
              {product.title}
            </h1>
            {product.artist && <p className="text-muted-foreground mt-1 text-sm">by {product.artist}</p>}
            {reviewStats.total > 0 && (
              <div className="flex items-center gap-2 mt-2">
                <StarRating rating={Math.round(reviewStats.average_rating)} size="w-3.5 h-3.5" />
                <span className="text-sm text-muted-foreground">{reviewStats.average_rating} ({reviewStats.total} reviews)</span>
              </div>
            )}
          </div>

          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="product-description">
            {product.description}
          </p>

          {/* Price */}
          <div>
            <p className="font-heading text-3xl font-bold" data-testid="product-price">${currentPrice.toFixed(2)}</p>
            {product.stock < 20 && <p className="text-xs text-fire mt-1 font-medium">Only {product.stock} left in stock!</p>}
          </div>

          {/* Size Selection */}
          {product.sizes?.length > 0 && (
            <div>
              <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Select Size</h2>
              <div className="flex flex-wrap gap-2 mt-2">
                {product.sizes.map((size) => (
                  <button key={size.size} onClick={() => setSelectedSize(size)}
                    className={`px-3 py-2 border-2 text-sm transition-all rounded ${
                      selectedSize?.size === size.size
                        ? 'border-orange-500 bg-orange-500 text-white'
                        : 'border-border hover:border-orange-400'
                    }`} data-testid={`size-${size.size}`}>
                    <span className="font-medium">{size.size}"</span>
                    <span className="ml-1.5 text-xs opacity-80">${size.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Quantity</h2>
            <div className="flex items-center gap-3 mt-2">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-2 border border-border hover:border-orange-400 transition-colors rounded" data-testid="quantity-decrease">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="font-heading text-lg w-10 text-center" data-testid="quantity-value">{quantity}</span>
              <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                className="p-2 border border-border hover:border-orange-400 transition-colors rounded" data-testid="quantity-increase">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button onClick={handleAddToCart} disabled={addingToCart || product.stock === 0}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold rounded-lg hover:from-red-600 hover:to-orange-600 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-to-cart-btn">
              {addingToCart ? 'Adding...' : <><ShoppingCart className="w-5 h-5" /> Add to Cart</>}
            </button>
            <button onClick={handleWishlistToggle}
              className={`px-5 py-3.5 border-2 transition-all flex items-center justify-center gap-2 rounded-lg font-medium ${
                inWishlist ? 'border-fire bg-fire text-white' : 'border-border hover:border-fire hover:text-fire'
              }`} data-testid="wishlist-btn">
              <Heart className={`w-5 h-5 ${inWishlist ? 'fill-current' : ''}`} />
              {inWishlist ? 'In Wishlist' : 'Wishlist'}
            </button>
          </div>

          {/* Shipping Info */}
          <div className="border border-border rounded-lg p-4 space-y-3 mt-2" data-testid="shipping-info">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Truck className="w-4.5 h-4.5 text-green-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">USA Fast Priority Shipping</h3>
                <p className="text-xs text-muted-foreground">Free shipping on orders over $50</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-4.5 h-4.5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Ships in a Protective Tube</h3>
                <p className="text-xs text-muted-foreground">Rolled &amp; shipped safely to prevent damage</p>
              </div>
            </div>
          </div>

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Tags: {product.tags.map((tag, i) => (
                  <Link key={tag} to={`/shop?search=${tag}`} className="hover:text-fire transition-colors">
                    {tag}{i < product.tags.length - 1 ? ', ' : ''}
                  </Link>
                ))}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Reviews Section */}
      <div className="mt-16 border-t border-border pt-10" data-testid="reviews-section">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="font-heading text-2xl font-bold">Customer Reviews</h2>
            {reviewStats.total > 0 && (
              <div className="flex items-center gap-2 mt-1">
                <StarRating rating={Math.round(reviewStats.average_rating)} />
                <span className="text-sm text-muted-foreground">{reviewStats.average_rating} out of 5 ({reviewStats.total} reviews)</span>
              </div>
            )}
          </div>
        </div>

        {isAuthenticated && (
          <div className="bg-muted/30 border border-border rounded-lg p-5 mb-8" data-testid="write-review">
            <h3 className="font-bold text-sm mb-3">Write a Review</h3>
            <div className="mb-3">
              <label className="text-xs text-muted-foreground block mb-1">Rating</label>
              <StarRating rating={newReview.rating} interactive onRate={(r) => setNewReview({ ...newReview, rating: r })} />
            </div>
            <textarea value={newReview.comment} onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
              placeholder="Share your experience with this poster..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-orange-500"
              data-testid="review-comment-input" />
            <button onClick={handleSubmitReview} disabled={submittingReview}
              className="mt-2 px-5 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-sm font-bold rounded-lg hover:from-red-600 hover:to-orange-600 disabled:opacity-50"
              data-testid="submit-review-btn">
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </button>
            <p className="text-[10px] text-muted-foreground mt-1">Only verified purchasers can submit reviews.</p>
          </div>
        )}

        <div className="space-y-4" data-testid="reviews-list">
          {reviews.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">No reviews yet. Be the first to review!</p>
          ) : (
            reviews.map(review => (
              <div key={review.id} className="border border-border rounded-lg p-4" data-testid={`review-${review.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-xs font-bold text-white">
                      {review.user_name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{review.user_name}</p>
                        {review.verified_purchase && (
                          <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Verified Purchase</span>
                        )}
                      </div>
                      <StarRating rating={review.rating} size="w-3 h-3" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground">{new Date(review.created_at).toLocaleDateString()}</span>
                    {isAdmin && (
                      <button onClick={() => handleDeleteReview(review.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors" data-testid={`delete-review-${review.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{review.comment}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
